import React, { useState } from 'react';
import {
    Card,
    CardContent,
    Checkbox,
    Typography,
    Box,
    Chip,
    CardMedia,
    Skeleton,
    Stack,
    IconButton,
} from '@mui/material';
import { ThspianAvatar } from '../Avatar/ThspianAvatar';
import { IVideoSubmission } from './types';
import { ThemeContext } from '../../module/ThemeManager';
import { ClockIcon } from '@mui/x-date-pickers';
import { CheckCircle, PlayCircle, Image as ImageIcon, Favorite, Close } from '@mui/icons-material';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import { PreselectionConfirmationDialog } from './PreselectionConfirmationDialog';
import { theme } from '../../constants/theme';
import { DialogueSheet } from '../DialogueSheet/DialogueSheet';
import { useDispatch } from 'react-redux';
import { setmedia } from '../../redux/project/actions';

interface IVideoCardProps {
    video: IVideoSubmission;
    selected: boolean;
    onSelect: (videoId: string, checked: boolean) => void;
    onPreselect?: (submissionIds: string[], selection_state: 'selected' | 'not_selected') => void;
}

const getCategoryColor = (category: string) => {
    const colors: { [key: string]: string } = {
        picture: 'secondary',
        skit: 'secondary',
        poster: 'secondary',
        video: 'secondary',
        audio: 'secondary',
        default: 'secondary',
    };
    return colors[category] || colors.default;
};

const formatDuration = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

const getScoreColor = (score?: number): string => {
    if (score && score >= 4.5) return 'success';
    if (score && score >= 3.5) return 'warning';
    return 'error';
};

export const VideoCard: React.FC<IVideoCardProps> = ({ video, selected, onSelect, onPreselect }) => {
    const [imageLoaded, setImageLoaded] = React.useState(false);
    const [imageError, setImageError] = React.useState(false);
    const [showConfirmation, setShowConfirmation] = React.useState(false);
    const [pendingAction, setPendingAction] = React.useState<'select' | 'deselect' | null>(null);
    const [showSelectionModal, setShowSelectionModal] = useState(false);
    const [selectionState, setSeectionState] = useState<'selected' | 'not_selected'>();
    const dispatch = useDispatch();

    // Reset states when video changes
    React.useEffect(() => {
        setImageLoaded(false);
        setImageError(false);
    }, [video._id]);

    const handleSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const checked = event.target.checked;
        const action = checked ? 'select' : 'deselect';

        // Check if email has been sent and we're trying to deselect
        if (!checked && video.selection_email_sent) {
            setPendingAction(action);
            setShowConfirmation(true);
            return;
        }

        // Direct deselection if no email sent
        onSelect(video._id, checked);
    };

    const handleConfirmAction = () => {
        if (selectionState && onPreselect) {
            const selection_state = selectionState === 'selected' ? 'selected' : 'not_selected';
            onPreselect([video._id], selection_state);
        }
        setShowConfirmation(false);
        setShowSelectionModal(false);
        setPendingAction(null);
    };

    const handleCloseConfirmation = () => {
        setShowConfirmation(false);
        setPendingAction(null);
    };

    const handleOpenSelectionModal = (selectState?: 'selected' | 'not_selected') => {
        setSeectionState(selectState);
        setShowSelectionModal(!showSelectionModal);
    };

    // Check if feature_image is available
    const hasFeatureImage = video.project.feature_image && video.project.feature_image.trim() !== '';

    // Determine if placeholder should be shown
    const showPlaceholder = !hasFeatureImage || !imageLoaded || imageError;

    const handlePreviewFile = () => {
        dispatch(
            setmedia({
                media: video.project,
                mediaType: 'act',
            }),
        );
    };

    return (
        <ThemeContext.Consumer>
            {({ themeStyle }) => (
                <Card
                    sx={{
                        position: 'relative',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease-in-out',
                        border: selected ? '2px solid' : '1px solid',
                        borderColor: selected ? 'primary.main' : 'divider',
                        boxShadow: selected ? 3 : 1,
                        '&:hover': {
                            boxShadow: 4,
                            transform: 'translateY(-2px)',
                        },
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                    }}>
                    {/* Media Container */}
                    <Box sx={{ position: 'relative' }}>
                        <CardMedia
                            component="img"
                            sx={{
                                height: 200,
                                aspectRatio: '16/9',
                                objectFit: 'cover',
                                display: imageLoaded && !imageError ? 'block' : 'none',
                                borderRadius: '8px 8px 0 0',
                            }}
                            image={video?.project?.feature_image}
                            alt={video.project.title}
                            onError={() => setImageError(true)}
                            onLoad={() => {
                                setImageLoaded(true);
                                setImageError(false);
                            }}
                        />

                        {/* Placeholder Fallback */}
                        {showPlaceholder && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: 'grey.200',
                                    flexDirection: 'column',
                                    gap: 1,
                                    display: 'flex',
                                    borderRadius: '8px 8px 0 0',
                                }}>
                                {hasFeatureImage && !imageError ? (
                                    <Skeleton
                                        variant="rectangular"
                                        width="100%"
                                        height="100%"
                                        sx={{ borderRadius: '8px 8px 0 0' }}
                                    />
                                ) : (
                                    <>
                                        <ImageIcon sx={{ fontSize: 48, color: 'grey.400' }} />
                                        <Typography variant="caption" color="text.secondary">
                                            {!hasFeatureImage
                                                ? 'No Image Available'
                                                : imageError
                                                ? 'Failed to Load'
                                                : 'Loading...'}
                                        </Typography>
                                    </>
                                )}
                            </Box>
                        )}

                        {/* Checkbox top-right */}
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 12,
                                right: 12,
                                zIndex: 10,
                                backgroundColor: theme[themeStyle].darkGray60,
                                borderRadius: '50%',
                                padding: '2px',
                            }}>
                            <Checkbox
                                checked={selected}
                                onChange={handleSelect}
                                disabled={video.selection_email_sent}
                                sx={{
                                    '&.Mui-checked': {
                                        color: 'primary.main',
                                    },
                                    '&.Mui-disabled': {
                                        opacity: 0.6,
                                        cursor: 'not-allowed',
                                    },
                                }}
                                title={video.selection_email_sent ? 'Selection email already sent - cannot modify' : ''}
                            />
                        </Box>

                        {/* Category Chip top-left */}
                        <Box sx={{ position: 'absolute', top: 12, left: 12, zIndex: 10 }}>
                            <Chip
                                label={video.project.project_type}
                                color={getCategoryColor(video.project.project_type) as any}
                                size="small"
                                variant="filled"
                                sx={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
                            />
                        </Box>

                        {/* Play Button Center Overlay */}
                        <Box
                            sx={{
                                position: 'absolute',
                                inset: 0,
                                backgroundColor: 'rgba(0, 0, 0, 0.2)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                opacity: 0,
                                transition: 'opacity 0.2s ease-in-out',
                                '&:hover': {
                                    opacity: 1,
                                },
                            }}>
                            <IconButton
                                onClick={handlePreviewFile}
                                sx={{
                                    backgroundColor: 'rgba(0, 0, 0, 0.4)',
                                    width: 48,
                                    height: 48,
                                }}>
                                <PlayCircle sx={{ color: 'white' }} />
                            </IconButton>
                        </Box>

                        {/* Duration Badge bottom-right */}
                        <Box
                            sx={{
                                position: 'absolute',
                                bottom: 12,
                                right: 12,
                                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                                color: 'white',
                                px: 1,
                                py: 0.5,
                                borderRadius: 1,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                            }}>
                            <ClockIcon sx={{ fontSize: 16 }} />
                            <Typography variant="caption">
                                {formatDuration(Math.floor(video?.project?.metadata?.duration || 0 * 60))}
                            </Typography>
                        </Box>
                    </Box>

                    {/* Content */}
                    <CardContent
                        sx={{
                            p: 3,
                            flexGrow: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            backgroundColor: theme[themeStyle].deep_grey_lighter,
                        }}>
                        {/* Score */}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CheckCircle
                                    sx={{
                                        color: getScoreColor(video.total_average),
                                        fontSize: 20,
                                    }}
                                />
                                <Typography
                                    variant="body2"
                                    color={getScoreColor(video?.total_average)}
                                    fontWeight="bold">
                                    {video?.total_average?.toFixed(1)}
                                </Typography>
                            </Box>
                        </Box>

                        {/* Title */}
                        <Typography
                            variant="h6"
                            onClick={handlePreviewFile}
                            component="h3"
                            sx={{
                                mb: 1,
                                fontWeight: 600,
                                lineHeight: 1.3,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                color: theme[themeStyle].tag_white,
                            }}>
                            {video.project.title}
                        </Typography>

                        {/* Description */}
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                                mb: 2,
                                flexGrow: 1,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                color: theme[themeStyle].tag_white,
                            }}>
                            {video?.project?.description}
                        </Typography>

                        {/* Stats */}
                        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                            <Typography variant="caption" sx={{ color: theme[themeStyle].tag_white }}>
                                {video?.total_votes} votes
                            </Typography>
                            <Typography variant="caption" sx={{ color: theme[themeStyle].tag_white }}>
                                {video?.project.view_count} views
                            </Typography>
                        </Box>

                        {/* Creator Info */}
                        <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
                            {video?.project?.created_by && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 'auto' }}>
                                    <ThspianAvatar
                                        user={video.project.created_by}
                                        sx={{ width: 32, height: 32 }}
                                        enableDefaultNavigation
                                    />
                                    <Box sx={{ flexGrow: 1 }}>
                                        <Typography
                                            variant="body2"
                                            fontWeight="medium"
                                            sx={{ color: theme[themeStyle].tag_white }}>
                                            {video?.project?.created_by?.firstName}{' '}
                                            {video?.project?.created_by.lastName}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: theme[themeStyle].tag_white }}>
                                            {new Date(video.project.createdAt!).toLocaleDateString()}
                                        </Typography>
                                    </Box>
                                </Box>
                            )}

                            <Box>
                                <IconButton
                                    aria-label="add to selected"
                                    disabled={video.selection_email_sent}
                                    onClick={() => handleOpenSelectionModal('selected')}>
                                    {video.selection_state === 'selected' ? (
                                        <Favorite
                                            sx={{
                                                color: theme[themeStyle].switchGreen,
                                            }}
                                        />
                                    ) : (
                                        <FavoriteBorderIcon sx={{ color: theme[themeStyle].tag_white }} />
                                    )}
                                </IconButton>
                                <IconButton
                                    aria-label="add to not selected"
                                    disabled={video.selection_email_sent}
                                    onClick={() => handleOpenSelectionModal('not_selected')}>
                                    <Close
                                        sx={{
                                            color:
                                                video.selection_state === 'not_selected'
                                                    ? theme[themeStyle].primary_red
                                                    : theme[themeStyle].tag_white,
                                        }}
                                    />
                                </IconButton>
                            </Box>
                        </Stack>

                        {/* Email Status Indicator */}
                        {video.selection_email_sent && (
                            <Box sx={{ mt: 2, textAlign: 'center' }}>
                                <Chip
                                    label="Email Sent"
                                    size="small"
                                    color="success"
                                    variant="outlined"
                                    sx={{ fontSize: '0.7rem' }}
                                />
                            </Box>
                        )}
                    </CardContent>

                    <DialogueSheet
                        open={showSelectionModal}
                        onClose={handleOpenSelectionModal}
                        onAgreeClicked={handleConfirmAction}
                        onDisagreeClick={handleOpenSelectionModal}
                        positiveText="Yes, Proceed"
                        negativeText="No, Cancel"
                        header={selectionState === 'selected' ? 'Confirm as Official Selection?' : 'Confirm Rejection'}
                        body={
                            selectionState === 'selected'
                                ? 'You are about to include this entry in the official selection. You can still make changes until the official email is sent.'
                                : 'You are about to confirm that this entry will not be part of the official selection. You can still make changes until the official email is sent.'
                        }
                    />

                    {/* Preselection Confirmation Dialog */}
                    <PreselectionConfirmationDialog
                        open={showConfirmation}
                        onClose={handleCloseConfirmation}
                        onConfirm={handleConfirmAction}
                        submissionTitle={video.project.title}
                        action={pendingAction || 'select'}
                        isEmailSent={video.selection_email_sent === true}
                    />
                </Card>
            )}
        </ThemeContext.Consumer>
    );
};
